import { ReactNode } from "react";
import classNames from "classnames";
import { currencyFormatter } from "@common/helpers/currency";
import { ScheduleApplicationListMilestone } from "@common/schemas/domains/accounting/scheduleApplicationList.schema";
import { BreakableText } from "@design-system/BreakableText/BreakableText";
import { DatePicker } from "@design-system/DatePicker/DatePicker";
import {
    getMilestoneAchievedRowKey,
    MilestonePostingDates,
} from "./milestoneAchievedDates";
import styles from "./MilestoneAchievedModal.module.scss";

export interface MilestoneAchievedBulkRow {
    applicationId: string;
    entityId: number;
    scheduleName: string;
    amount: number;
    milestone: ScheduleApplicationListMilestone;
}

export function formatMilestoneAchievedRowLabel(
    milestone: Pick<ScheduleApplicationListMilestone, "sortOrder" | "name">,
): string {
    return `Milestone ${milestone.sortOrder} · ${milestone.name}`;
}

interface MilestoneAchievedRowDates {
    /** Resolved posting date per row key. */
    dates: MilestonePostingDates;
    isDisabled: boolean;
    onDateChange: (options: { rowKey: string; date: string }) => void;
}

interface MilestoneAchievedColumn {
    key: string;
    header: string;
    className?: string;
    render: (row: MilestoneAchievedBulkRow) => ReactNode;
}

function buildColumns({
    dates,
    isDisabled,
    onDateChange,
}: MilestoneAchievedRowDates): MilestoneAchievedColumn[] {
    return [
        {
            key: "source",
            header: "Source",
            className: styles.sourceCell,
            render: (row) => (
                <BreakableText text={row.scheduleName} showTooltip />
            ),
        },
        {
            key: "milestone",
            header: "Milestone",
            className: styles.milestoneCell,
            render: (row) => formatMilestoneAchievedRowLabel(row.milestone),
        },
        {
            key: "amount",
            header: "Amount",
            className: styles.amountCell,
            render: (row) => currencyFormatter.format(row.amount),
        },
        {
            key: "postingDate",
            header: "Posting date",
            className: styles.dateCell,
            render: (row) => {
                const rowKey = getMilestoneAchievedRowKey(row);
                return (
                    <DatePicker
                        mode="day"
                        variant="panel-input"
                        clearable={false}
                        disabled={isDisabled}
                        buttonProps={{
                            "aria-label": `Posting date for ${row.scheduleName}, milestone ${row.milestone.sortOrder}`,
                        }}
                        value={dates[rowKey] ?? ""}
                        onValueChange={(value) =>
                            onDateChange({ rowKey, date: value ?? "" })
                        }
                    />
                );
            },
        },
    ];
}

/**
 * What the bulk confirm posts: the amounts were fixed at apply, only each
 * row's posting date is editable, and a batch date disables them.
 */
export const MilestoneAchievedRowsList = ({
    rows,
    ...rowDates
}: MilestoneAchievedRowDates & { rows: MilestoneAchievedBulkRow[] }) => {
    const columns = buildColumns(rowDates);
    return (
        <div className={styles.rows}>
            <table className={styles.table}>
                <thead>
                    <tr>
                        {columns.map((column) => (
                            <th
                                key={column.key}
                                scope="col"
                                className={classNames(
                                    styles.headerCell,
                                    column.className,
                                )}
                            >
                                {column.header}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => (
                        <tr key={getMilestoneAchievedRowKey(row)}>
                            {columns.map((column) => (
                                <td
                                    key={column.key}
                                    className={classNames(
                                        styles.cell,
                                        column.className,
                                    )}
                                >
                                    {column.render(row)}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};
